import React, { useEffect, useState, useCallback, useContext } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { useDataTableContext } from '../shared/Context';
import { DataTableContext } from '../../DataTable/DataTableState';

interface CheckboxRendererProps extends ICellRendererParams {
  primaryKey?: string;
  hasRowHeightOption?: boolean;
}

const CheckboxRenderer: React.FC<CheckboxRendererProps> = (props) => {
  const [checked, setChecked] = useState<boolean>(props.node.isSelected());
  const { setSelectedRowCount, isSelectAllActive, totalEntriesRef, selectedRowData, deSelectedRowData } = useDataTableContext();
  const { primaryKey }  = props ;
  const DataTableContextObj = useContext(DataTableContext);
  const hasRowHeightOption = props.hasRowHeightOption || false;
  const getRowKey = useCallback((data: any) => {
    return primaryKey ? data?.[primaryKey] : JSON.stringify(data);
  }, [primaryKey]);

  useEffect(() => {
    // console.log('CheckboxRenderer useEffect', props.node.isSelected(), props.node.selectable, props.node.data);
    const data = props.node.data;
    if (!data) return;

    const key = getRowKey(data);
    if (key == null) return;

    let shouldBeSelected = false;

    if (isSelectAllActive.current) {
      shouldBeSelected = !deSelectedRowData.current.has(key);
    } else {
      shouldBeSelected = selectedRowData.current.has(key);
    }

    if (props.node.isSelected() !== shouldBeSelected) {
      props.node.setSelected(shouldBeSelected, false); 
    }

    setChecked(shouldBeSelected);
  }, [props.node, getRowKey, isSelectAllActive, DataTableContextObj]);

  useEffect(() => {
    const listener = () => {
      setChecked(props.node.isSelected());
    };

    props.node.addEventListener('rowSelected', listener);
    return () => {
      props.node.removeEventListener('rowSelected', listener);
    };
  }, [props.node]);

  const toggleSelection = useCallback(() => {
    if (!props.node || !props.node.selectable) {
      return;
    }
    const newChecked = !checked;
    const data = props.node.data;
    
    if (!data) {
      return;
    }

    props.node.setSelected(newChecked);

    const key = getRowKey(data);
    if (key == null) {
      return;
    }

    const newSelectedMap = new Map(selectedRowData.current ?? new Map());
    const newDeSelectedMap = new Map(deSelectedRowData.current ?? new Map());

    if (isSelectAllActive.current) {
      if (newChecked) {
        newDeSelectedMap.delete(key);
        newSelectedMap.set(key, data);
        deSelectedRowData.current = newDeSelectedMap;
        selectedRowData.current = newSelectedMap;
        setSelectedRowCount(totalEntriesRef.current - newDeSelectedMap.size);
      } else {
        newDeSelectedMap.set(key, data);
        newSelectedMap.delete(key);
        deSelectedRowData.current = newDeSelectedMap;
        selectedRowData.current = newSelectedMap;
        setSelectedRowCount(totalEntriesRef.current - newDeSelectedMap.size);
      }
    } else {
      if (newChecked) {
        newSelectedMap.set(key, data);
      } else {
        newSelectedMap.delete(key);
      }

      selectedRowData.current = newSelectedMap;
      deSelectedRowData.current = new Map(); 
      setSelectedRowCount(newSelectedMap.size);
    }

    // console.log('CheckboxRenderer toggleSelection',selectedRowData.current.size, deSelectedRowData.current.size);
    
    props.api.dispatchEvent({ type: 'selectionChanged' });
  }, [ checked, props.node, props.api, getRowKey, selectedRowData,  deSelectedRowData, isSelectAllActive, totalEntriesRef, setSelectedRowCount ]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: hasRowHeightOption ? undefined : 'center',
        paddingTop: hasRowHeightOption ? "4px" : undefined,
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <input
        type="checkbox"
        className="grey-checkbox"
        checked={checked}
        onChange={toggleSelection}
        disabled={!props.node.selectable}
        style={{
          width: '16px',
          height: '16px',
          cursor: props.node.selectable ? 'pointer' : 'default',
        }}
      />
    </div>
  );
};

export default CheckboxRenderer;